import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { FileText, Upload, Trash2, Download, FileCheck } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

function fmtBytes(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Vault() {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/ip/assets");
        setAssets(data);
        if (data.length > 0 && !selected) setSelected(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadFiles();
    // eslint-disable-next-line
  }, [selected]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vault/list/${selected}`);
      setFiles(data);
    } finally {
      setLoading(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    const form = new FormData();
    form.append("file", file);
    form.append("asset_id", selected);
    form.append("label", label);
    setUploading(true);
    try {
      await api.post("/vault/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded "${file.name}"`);
      setLabel("");
      e.target.value = "";
      loadFiles();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const download = async (f) => {
    try {
      const resp = await api.get(`/vault/download/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try {
      await api.delete(`/vault/${f.id}`);
      toast.success("Deleted");
      loadFiles();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const selectedAsset = assets.find((a) => a.id === selected);

  return (
    <div className="p-6 md:p-8 fade-in-up" data-testid="vault-page">
      <div className="mb-8">
        <div className="eyebrow">Document Vault</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight mt-1">
          Secure Records
        </h1>
        <p className="text-slate-600 mt-2 text-sm md:text-base">
          Upload invention disclosures, contracts, and filing receipts — bank-grade storage per asset.
        </p>
      </div>

      <div className="bengal-card p-5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[240px]">
          <label className="eyebrow block mb-1.5">IP Asset</label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="vault-asset-select" className="w-full">
              <SelectValue placeholder="Select an asset" />
            </SelectTrigger>
            <SelectContent>
              {assets.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="eyebrow block mb-1.5">Label (optional)</label>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Office action response, signed NDA"
            data-testid="vault-label-input"
            className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm"
          />
        </div>
        <label className={`bengal-btn-primary text-sm flex items-center gap-2 cursor-pointer ${uploading ? "opacity-50" : ""}`} data-testid="vault-upload-btn">
          <Upload size={15} /> {uploading ? "Uploading…" : "Upload file"}
          <input type="file" onChange={upload} disabled={uploading || !selected} className="hidden" />
        </label>
      </div>

      {selectedAsset && (
        <div className="eyebrow text-slate-500 mb-3">Files for <span className="text-slate-800">{selectedAsset.title}</span></div>
      )}

      <div className="bengal-card">
        <table className="w-full text-sm" data-testid="vault-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-3 px-4 text-left">File</th>
              <th className="py-3 px-4 text-left">Label</th>
              <th className="py-3 px-4 text-left">Type</th>
              <th className="py-3 px-4 text-right">Size</th>
              <th className="py-3 px-4 text-left">Uploaded</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-8 text-slate-500">Loading…</td></tr>}
            {!loading && files.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-slate-500">
                <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                No files yet for this asset. Upload above.
              </td></tr>
            )}
            {files.map((f) => (
              <tr key={f.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`vault-row-${f.id}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <FileCheck size={15} className="text-[#B33A3A] shrink-0" />
                    <span className="font-medium truncate max-w-[280px]">{f.original_filename}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-slate-600">{f.label || "—"}</td>
                <td className="py-3 px-4 text-xs font-mono text-slate-500">{f.content_type}</td>
                <td className="py-3 px-4 text-right font-mono text-xs">{fmtBytes(f.size)}</td>
                <td className="py-3 px-4 text-xs text-slate-500">{new Date(f.created_at).toLocaleDateString()}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => download(f)} data-testid={`dl-${f.id}`} className="p-1.5 text-slate-500 hover:text-[#B33A3A] hover:bg-red-50 rounded-sm" title="Download">
                      <Download size={15} />
                    </button>
                    <button onClick={() => del(f)} data-testid={`rm-${f.id}`} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm" title="Delete">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
